import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, query, where, addDoc, deleteDoc, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Activity, Folder, FolderOpen, ChevronRight, ChevronDown,
  LayoutGrid, Inbox, Plus, Search, X,
  Clock, List, Pencil, MonitorPlay, Copy, Trash2, ImagePlus, Minus, Search as SearchIcon, Tag as TagIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFolders, createFolder, renameFolder, deleteFolder, moveProgramToFolder } from '../hooks/useFolders';
import { tagColor, tagBg, MAX_TAGS } from '../utils/tagUtils';
import TagEditor from '../components/TagEditor';
import CoverImageEditor from '../components/CoverImageEditor';

// ============================================================================
// Programs — library/bookshelf page (Piece 5a cover + tags + search/tag-filter).
//
// Piece 1 (done): shell + folder tree + New Folder. Programs getDocs preserved.
// Piece 4a (THIS): wire New Program + Edit -> ProgramBuilder (react-router navigate).
//   (routes registered in App.js). Folder move / rename / delete / duplicate /
//   delete-program / assign-to-screen still STUBBED (later 4x sub-steps).
// Piece 3 (done): right-rail detail panel for the selected program — cover, name,
//   FOLDER PATH (where it actually lives, from folderId — independent of the name),
//   tags, duration, clips, optional notes, and an action stack (Edit / Assign to
//   Screen / Duplicate / Delete) STUBBED for Piece 4. Consumes selectedProgramId.
// Piece 2 (done): real program cards in the center, filtered by selected folder;
//   card = clean branded placeholder thumbnail + name + tags +
//   duration + N clips; click selects (feeds Piece 3); empty state; New Program card.
//   Lightweight card style (NOT the heavy .card padding).
//   - NOT in this piece: detail-panel CONTENT (Piece 3 — selection state wired here),
//     rename/delete/move (Piece 4), cover upload, search/sort/list-view.
//
// Real Pulse tokens: DM Sans, #E63946, square corners, lucide icons,
// .btn/.btn-primary/.btn-secondary, .modal*, .form-* classes.
// ============================================================================

// Virtual bucket ids (NOT Firestore folders):
const ALL = '__all__';
const UNFILED = '__unfiled__';

function Programs() {
  const { currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();

  // --- programs: PRESERVED existing one-shot getDocs behavior (unchanged) ---
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  // one-shot fetch (unchanged behavior), but extracted so it can be re-called after a move
  const fetchPrograms = async () => {
    try {
      const programsQuery = isAdmin
        ? collection(db, 'programs')
        : query(collection(db, 'programs'), where('userId', '==', currentUser?.uid));

      const snapshot = await getDocs(programsQuery);
      const data = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(program => {
          if (isAdmin) return !program.userId || program.userId === currentUser?.uid;
          return program.userId === currentUser?.uid;
        });
      setPrograms(data);
    } catch (error) {
      console.error('Error fetching programs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) fetchPrograms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, isAdmin]);

  // load the user's screens for the assign picker (owner-scoped, mirrors Screens.js)
  useEffect(() => {
    if (!currentUser) return;
    const load = async () => {
      try {
        const q = isAdmin
          ? collection(db, 'screens')
          : query(collection(db, 'screens'), where('userId', '==', currentUser?.uid));
        const snap = await getDocs(q);
        const data = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(sc => isAdmin ? (!sc.userId || sc.userId === currentUser?.uid) : sc.userId === currentUser?.uid);
        setScreens(data);
      } catch (e) { console.error('Error fetching screens:', e); }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, isAdmin]);

  // --- folders: realtime (Piece 1 addition) ---
  const { folders, loading: foldersLoading } = useFolders();

  // --- selected folder (virtual ALL / UNFILED, or a real folderId) ---
  const [selectedFolder, setSelectedFolder] = useState(ALL);
  const [selectedProgramId, setSelectedProgramId] = useState(null);  // feeds Piece 3
  const [searchText, setSearchText] = useState('');
  const [filterMode, setFilterMode] = useState('name');
  const [tagFilter, setTagFilter] = useState([]);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const filterBarRef = useRef(null);

  // close the tag-filter dropdown on outside click (equipment/assets pattern)
  useEffect(() => {
    if (!tagDropdownOpen) return;
    const onDown = (e) => { if (filterBarRef.current && !filterBarRef.current.contains(e.target)) setTagDropdownOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [tagDropdownOpen]);
  const [expanded, setExpanded] = useState({});   // { [topFolderId]: true }

  // --- New Folder modal state ---
  const [folderModal, setFolderModal] = useState(null); // null | { parentId: null|id }
  const [newFolderName, setNewFolderName] = useState('');
  const [savingFolder, setSavingFolder] = useState(false);
  const [folderError, setFolderError] = useState(null);

  // --- move-program (change folder) state ---
  const [moveModal, setMoveModal] = useState(null);   // null | { program }
  const [movingId, setMovingId] = useState(null);     // folderId being moved to (or '__unfiled__')
  const [moveError, setMoveError] = useState(null);

  // --- rename folder state ---
  const [renameModal, setRenameModal] = useState(null);
  const [renameName, setRenameName] = useState('');
  const [savingRename, setSavingRename] = useState(false);
  const [renameError, setRenameError] = useState(null);

  // --- delete folder state ---
  const [deleteModal, setDeleteModal] = useState(null);
  const [deletingFolder, setDeletingFolder] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // --- duplicate / delete PROGRAM state ---
  const [busyProgram, setBusyProgram] = useState(false);
  const [deleteProgramModal, setDeleteProgramModal] = useState(null);
  const [programActionError, setProgramActionError] = useState(null);

  // --- assign-to-screen state ---
  const [screens, setScreens] = useState([]);
  const [assignModal, setAssignModal] = useState(null);   // null | { program }
  const [assigningId, setAssigningId] = useState(null);   // screenId being assigned
  const [assignError, setAssignError] = useState(null);
  const [assignedOk, setAssignedOk] = useState(null);     // screenId just assigned (for ✓)

  // --- cover image editor state ---
  const [coverModal, setCoverModal] = useState(null);   // null | { program }
  const [tagsModal, setTagsModal] = useState(null);     // null | { program }

  // --- derive the 2-level tree from the flat folder list ---
  const topFolders = folders.filter(f => f.parentFolderId == null);
  const childrenOf = (id) => folders.filter(f => f.parentFolderId === id);

  // --- program counts per bucket/folder (from the preserved programs list) ---
  const countAll = programs.length;
  const countUnfiled = programs.filter(p => !p.folderId).length;
  const countFolder = (id) => {
    // a top-level folder's count includes its own programs + its children's
    const childIds = childrenOf(id).map(c => c.id);
    return programs.filter(p => p.folderId === id || childIds.includes(p.folderId)).length;
  };
  const countChild = (id) => programs.filter(p => p.folderId === id).length;

  // --- programs shown in the center grid, filtered by the selected folder ---
  // ALL -> everything; UNFILED -> folderId null; top-level folder -> own + children's;
  // child folder -> its own only. (Mirrors the count logic.)
  const visiblePrograms = (() => {
    let list;
    if (selectedFolder === ALL) list = programs;
    else if (selectedFolder === UNFILED) list = programs.filter(p => !p.folderId);
    else {
      const childIds = childrenOf(selectedFolder).map(c => c.id);
      list = childIds.length
        ? programs.filter(p => p.folderId === selectedFolder || childIds.includes(p.folderId))
        : programs.filter(p => p.folderId === selectedFolder);
    }
    if (filterMode === 'name' && searchText.trim()) {
      const query2 = searchText.trim().toLowerCase();
      list = list.filter(p => (p.name || '').toLowerCase().includes(query2));
    }
    if (tagFilter.length) {
      list = list.filter(p => {
        const pt = (Array.isArray(p.tags) ? p.tags : []).map(t => t.toLowerCase());
        return tagFilter.every(t => pt.includes(t.toLowerCase()));
      });
    }
    return list;
  })();

  // duration ms -> "M:SS" / "H:MM:SS"
  const fmtDuration = (ms) => {
    const total = Math.max(0, Math.round((ms || 0) / 1000));
    const h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), sec = total % 60;
    if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
    return `${m}:${String(sec).padStart(2,'0')}`;
  };

  const toggleExpand = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const openNewFolder = (parentId = null) => {
    setFolderModal({ parentId });
    setNewFolderName('');
    setFolderError(null);
  };

  const submitNewFolder = async () => {
    setFolderError(null);
    setSavingFolder(true);
    try {
      const newId = await createFolder(newFolderName, folderModal.parentId, currentUser);
      // auto-expand the parent if we just added a child
      if (folderModal.parentId) setExpanded(e => ({ ...e, [folderModal.parentId]: true }));
      setSelectedFolder(newId);
      setFolderModal(null);
    } catch (e) {
      setFolderError(e.message || 'Could not create folder.');
    } finally {
      setSavingFolder(false);
    }
  };

  // move the selected program into a folder (or Unfiled). folderId null = Unfiled.
  const doMoveProgram = async (folderId) => {
    if (!moveModal?.program) return;
    setMoveError(null);
    setMovingId(folderId || '__unfiled__');
    try {
      await moveProgramToFolder(moveModal.program.id, folderId, currentUser);
      await fetchPrograms();     // one-shot list: refresh so the move reflects immediately
      setMoveModal(null);
    } catch (e) {
      setMoveError(e.message || 'Could not move program.');
    } finally {
      setMovingId(null);
    }
  };

  const openRename = (folder) => { setRenameModal({ folder }); setRenameName(folder.name || ''); setRenameError(null); };
  const submitRename = async () => {
    setRenameError(null); setSavingRename(true);
    try {
      await renameFolder(renameModal.folder.id, renameName, currentUser);
      setRenameModal(null);
    } catch (e) {
      setRenameError(e.message || 'Could not rename folder.');
    } finally { setSavingRename(false); }
  };

  const openDelete = (folder) => { setDeleteModal({ folder }); setDeleteError(null); };
  const submitDelete = async () => {
    setDeleteError(null); setDeletingFolder(true);
    try {
      await deleteFolder(deleteModal.folder.id, currentUser);
      if (selectedFolder === deleteModal.folder.id) setSelectedFolder(ALL);
      setDeleteModal(null);
    } catch (e) {
      if (e.code === 'FOLDER_NOT_EMPTY') {
        setDeleteError("This folder isn't empty. Move or delete its programs and subfolders first.");
      } else {
        setDeleteError(e.message || 'Could not delete folder.');
      }
    } finally { setDeletingFolder(false); }
  };

  const duplicateProgram = async (program) => {
    if (!program || !currentUser) return;
    setProgramActionError(null); setBusyProgram(true);
    try {
      const ref = await addDoc(collection(db, 'programs'), {
        name: `Copy of ${program.name || 'Program'}`,
        userId: currentUser.uid,
        schemaVersion: program.schemaVersion || 1,
        totalDurationMs: program.totalDurationMs || 0,
        theme: program.theme || null,
        folderId: program.folderId || null,
        tags: Array.isArray(program.tags) ? program.tags : [],
        composition: Array.isArray(program.composition) ? program.composition : [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await fetchPrograms();
      setSelectedProgramId(ref.id);
    } catch (e) {
      setProgramActionError(e.message || 'Could not duplicate program.');
    } finally { setBusyProgram(false); }
  };

  const deleteProgram = async () => {
    const program = deleteProgramModal?.program;
    if (!program) return;
    setProgramActionError(null); setBusyProgram(true);
    try {
      await deleteDoc(doc(db, 'programs', program.id));
      if (selectedProgramId === program.id) setSelectedProgramId(null);
      await fetchPrograms();
      setDeleteProgramModal(null);
    } catch (e) {
      setProgramActionError(e.message || 'Could not delete program.');
    } finally { setBusyProgram(false); }
  };

  // Assign the program to a screen — reuses the EXISTING mechanism (Screens.js):
  // contentType:'program' + assignedContent:programId + assignedPlaylist:'' (cleared).
  const assignToScreen = async (screen) => {
    if (!assignModal?.program || !screen) return;
    setAssignError(null); setAssigningId(screen.id);
    try {
      await updateDoc(doc(db, 'screens', screen.id), {
        contentType: 'program',
        assignedContent: assignModal.program.id,
        assignedPlaylist: '',
      });
      // reflect locally so the picker shows the new assignment immediately
      setScreens(prev => prev.map(sc => sc.id === screen.id
        ? { ...sc, contentType: 'program', assignedContent: assignModal.program.id, assignedPlaylist: '' }
        : sc));
      setAssignedOk(screen.id);
      setTimeout(() => setAssignedOk(null), 2000);
    } catch (e) {
      setAssignError(e.message || 'Could not assign to screen.');
    } finally { setAssigningId(null); }
  };

  const saveCover = async (programId, cover) => {
    await updateDoc(doc(db, 'programs', programId), {
      coverImageUrl: cover.coverImageUrl,
      coverZoom: cover.coverZoom,
      coverPosX: cover.coverPosX,
      coverPosY: cover.coverPosY,
      updatedAt: serverTimestamp(),
    });
    // write confirmed by updateDoc -> update local state with the known values
    // (no full-collection refetch, which could read stale data and drop the change)
    setPrograms(prev => prev.map(p => p.id === programId ? {
      ...p,
      coverImageUrl: cover.coverImageUrl,
      coverZoom: cover.coverZoom,
      coverPosX: cover.coverPosX,
      coverPosY: cover.coverPosY,
    } : p));
  };

  const saveTags = async (programId, tags) => {
    await updateDoc(doc(db, 'programs', programId), { tags, updatedAt: serverTimestamp() });
    // write confirmed -> update local state directly (no stale-read refetch)
    setPrograms(prev => prev.map(p => p.id === programId ? { ...p, tags } : p));
  };

  // folder path for a program (where it actually lives), e.g. "Arm Care › Week 1"
  // or "Unfiled". Derived from folderId — independent of the program's name.
  const folderPathFor = (program) => {
    if (!program?.folderId) return 'Unfiled';
    const f = folders.find(x => x.id === program.folderId);
    if (!f) return 'Unfiled';
    if (f.parentFolderId) {
      const parent = folders.find(x => x.id === f.parentFolderId);
      return parent ? `${parent.name} › ${f.name}` : f.name;
    }
    return f.name;
  };

  const selectedProgram = programs.find(p => p.id === selectedProgramId) || null;

  // derived tag pool — every unique tag currently used across the user's programs
  const allTags = Array.from(new Set(programs.flatMap(p => Array.isArray(p.tags) ? p.tags : [])))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

  // label for the center-panel header (Piece 2 fills the grid; here just the header)
  const selectedLabel = (() => {
    if (selectedFolder === ALL) return 'All Programs';
    if (selectedFolder === UNFILED) return 'Unfiled';
    const f = folders.find(x => x.id === selectedFolder);
    if (!f) return 'Programs';
    if (f.parentFolderId) {
      const parent = folders.find(x => x.id === f.parentFolderId);
      return parent ? `${parent.name} › ${f.name}` : f.name;
    }
    return f.name;
  })();

  return (
    <div className="page-container">
      {/* ---- page header ---- */}
      <div className="page-header">
        <h1 className="page-title">Programs</h1>

        {/* inline search / tag-filter (Files/Assets pattern) */}
        <div style={S.filterBar} ref={filterBarRef}>
          <button style={{ ...S.filterIconBtn, ...(filterMode === 'name' ? S.filterIconActive : {}) }} title="Search by name" onClick={() => { setFilterMode('name'); setTagDropdownOpen(false); }}><SearchIcon size={16} /></button>
          <button style={{ ...S.filterIconBtn, ...(filterMode === 'tags' ? S.filterIconActive : {}) }} title="Filter by tags" onClick={() => { setFilterMode('tags'); setTagDropdownOpen(true); }}><TagIcon size={16} /></button>
          {filterMode === 'name' ? (
            <input style={S.filterInput} placeholder="Search programs…" value={searchText} onChange={e => setSearchText(e.target.value)} />
          ) : (
            <div style={S.filterTagArea} onClick={() => setTagDropdownOpen(true)}>
              {tagFilter.length === 0 && <span style={{ color: '#9aa0a8', fontSize: 14 }}>Filter by tags…</span>}
              {tagFilter.map(t => (
                <span key={t} style={{ ...S.tagChip, background: tagBg(t), color: tagColor(t) }}>
                  <span style={{ ...S.tagDot, background: tagColor(t) }} />{t}
                  <span style={S.tagX} onClick={(e) => { e.stopPropagation(); setTagFilter(tagFilter.filter(x => x !== t)); }}>×</span>
                </span>
              ))}
            </div>
          )}
          {(searchText || tagFilter.length > 0) && (
            <button style={S.filterClear} onClick={() => { setSearchText(''); setTagFilter([]); }} title="Clear"><X size={15} /></button>
          )}
          {filterMode === 'tags' && tagDropdownOpen && (
            <div style={S.filterTagDropdown}>
              {allTags.filter(t => !tagFilter.some(x => x.toLowerCase() === t.toLowerCase())).length === 0 ? (
                <div style={{ padding: '10px 12px', color: '#9aa0a8', fontSize: 13 }}>No more tags.</div>
              ) : (
                allTags.filter(t => !tagFilter.some(x => x.toLowerCase() === t.toLowerCase())).map(t => (
                  <div key={t} style={S.tagSuggestRow} onClick={() => setTagFilter([...tagFilter, t])}>
                    <span style={{ ...S.tagDot, background: tagColor(t) }} /><span style={{ flex: 1 }}>{t}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div style={S.headerActions}>
          <button className="btn btn-secondary" onClick={() => openNewFolder(null)}>
            <Plus size={16} /> New Folder
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/programs/new')}>
            <Plus size={16} /> New Program
          </button>
        </div>
      </div>

      {/* ---- three-column shell ---- */}
      <div style={S.cols}>

        {/* LEFT: folder tree */}
        <div className="card" style={S.leftPanel}>
          <div style={S.foldHead}>
            <span style={S.foldHeadLabel}>Folders</span>
            <button style={S.iconBtn} title="New folder" onClick={() => openNewFolder(null)}>
              <Plus size={16} />
            </button>
          </div>

          {/* virtual buckets */}
          <FolderRow
            icon={<LayoutGrid size={16} />}
            label="All Programs"
            count={countAll}
            active={selectedFolder === ALL}
            onClick={() => setSelectedFolder(ALL)}
          />
          <FolderRow
            icon={<Inbox size={16} />}
            label="Unfiled"
            count={countUnfiled}
            active={selectedFolder === UNFILED}
            onClick={() => setSelectedFolder(UNFILED)}
          />

          <div style={S.sep} />

          {foldersLoading ? (
            <div style={S.foldLoading}>Loading folders…</div>
          ) : topFolders.length === 0 ? (
            <div style={S.foldEmpty}>No folders yet. Create one to organize your programs.</div>
          ) : (
            topFolders.map(f => {
              const kids = childrenOf(f.id);
              const isOpen = !!expanded[f.id];
              return (
                <div key={f.id}>
                  <FolderRow
                    icon={isOpen && kids.length ? <FolderOpen size={16} /> : <Folder size={16} />}
                    caret={kids.length ? (isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />) : null}
                    label={f.name}
                    count={countFolder(f.id)}
                    active={selectedFolder === f.id}
                    onClick={() => setSelectedFolder(f.id)}
                    onCaret={kids.length ? (e) => { e.stopPropagation(); toggleExpand(f.id); } : null}
                    onAddChild={(e) => { e.stopPropagation(); openNewFolder(f.id); }}
                    onRename={(e) => { e.stopPropagation(); openRename(f); }}
                    onDelete={(e) => { e.stopPropagation(); openDelete(f); }}
                  />
                  {isOpen && kids.map(c => (
                    <FolderRow
                      key={c.id}
                      child
                      icon={<Folder size={15} />}
                      label={c.name}
                      count={countChild(c.id)}
                      active={selectedFolder === c.id}
                      onClick={() => setSelectedFolder(c.id)}
                      onRename={(e) => { e.stopPropagation(); openRename(c); }}
                      onDelete={(e) => { e.stopPropagation(); openDelete(c); }}
                    />
                  ))}
                </div>
              );
            })
          )}
        </div>

        {/* CENTER: card grid */}
        <div style={S.centerPanel}>
          <div style={S.centerHead}>
            <div style={S.centerTitle}>
              {selectedLabel}
              <span style={S.centerCount}>
                {loading ? '' : `${visiblePrograms.length} program${visiblePrograms.length === 1 ? '' : 's'}`}
              </span>
            </div>
          </div>

          {loading ? (
            <div className="card" style={S.gridPlaceholder}>Loading programs…</div>
          ) : (
            <div style={S.grid}>
              {visiblePrograms.map(p => (
                <ProgramCard
                  key={p.id}
                  program={p}
                  selected={p.id === selectedProgramId}
                  fmtDuration={fmtDuration}
                  onClick={() => setSelectedProgramId(p.id)}
                />
              ))}

              {/* New Program card (Piece 4 wires nav) */}
              <div
                style={S.newCard}
                onClick={() => navigate('/programs/new')}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#E63946'; e.currentTarget.style.color = '#E63946'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#d7dbe0'; e.currentTarget.style.color = '#9aa0a8'; }}
              >
                <Plus size={26} />
                <div style={{ fontWeight: 700, fontSize: 14 }}>New Program</div>
                <div style={{ fontSize: 12 }}>Create from scratch</div>
              </div>

              {/* empty state (only the New Program card shows when a folder has no programs) */}
              {visiblePrograms.length === 0 && (
                <div style={S.emptyGrid}>
                  No programs in <strong>{selectedLabel}</strong> yet.
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: detail panel */}
        <div className="card" style={S.rightPanel}>
          {selectedProgram ? (
            <ProgramDetail
              program={selectedProgram}
              folderPath={folderPathFor(selectedProgram)}
              fmtDuration={fmtDuration}
              onEdit={() => navigate(`/programs/${selectedProgram.id}/edit`)}
              onChangeFolder={() => { setMoveError(null); setMoveModal({ program: selectedProgram }); }}
              onAssign={() => { setAssignError(null); setAssignedOk(null); setAssignModal({ program: selectedProgram }); }}
              onChangeCover={() => setCoverModal({ program: selectedProgram })}
              onEditTags={() => setTagsModal({ program: selectedProgram })}
              onDuplicate={() => duplicateProgram(selectedProgram)}
              onDelete={() => { setProgramActionError(null); setDeleteProgramModal({ program: selectedProgram }); }}
              busy={busyProgram}
            />
          ) : (
            <div style={S.detailPlaceholder}>
              <Activity size={40} color="#c7ccd1" />
              <div style={{ marginTop: 10, color: '#9aa0a8', fontSize: 13, textAlign: 'center' }}>
                Select a program to see its details.
              </div>
            </div>
          )}
        </div>

      </div>

      {/* ---- New Folder modal (real Pulse .modal/.form conventions) ---- */}
      {folderModal && (
        <div className="modal-overlay" onClick={() => !savingFolder && setFolderModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                {folderModal.parentId ? 'New Subfolder' : 'New Folder'}
              </div>
              <button className="modal-close" onClick={() => !savingFolder && setFolderModal(null)}>×</button>
            </div>
            {folderModal.parentId && (
              <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
                Inside: <strong>{folders.find(f => f.id === folderModal.parentId)?.name}</strong>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Folder name</label>
              <input
                className="form-input"
                autoFocus
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && newFolderName.trim() && !savingFolder) submitNewFolder(); }}
                placeholder="e.g. Arm Care"
              />
            </div>
            {folderError && (
              <div style={{ color: '#E63946', fontSize: 13, marginBottom: 12 }}>{folderError}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn btn-secondary" onClick={() => setFolderModal(null)} disabled={savingFolder}>Cancel</button>
              <button className="btn btn-primary" onClick={submitNewFolder} disabled={savingFolder || !newFolderName.trim()}>
                {savingFolder ? 'Creating…' : 'Create Folder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Move program (change folder) modal ---- */}
      {moveModal && (
        <div className="modal-overlay" onClick={() => !movingId && setMoveModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Move “{moveModal.program.name || 'Program'}”</div>
              <button className="modal-close" onClick={() => !movingId && setMoveModal(null)}>×</button>
            </div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>Choose a destination folder:</div>

            <div style={S.movePicker}>
              {/* Unfiled */}
              <div
                style={{ ...S.moveRow, ...(!moveModal.program.folderId ? S.moveRowCurrent : {}) }}
                onClick={() => !movingId && doMoveProgram(null)}
              >
                <Inbox size={15} style={{ color: '#9aa0a8' }} />
                <span style={{ flex: 1 }}>Unfiled</span>
                {!moveModal.program.folderId && <span style={S.moveCurrent}>current</span>}
                {movingId === '__unfiled__' && <span style={S.moveCurrent}>moving…</span>}
              </div>

              {/* top-level folders + their children */}
              {topFolders.map(f => {
                const kids = childrenOf(f.id);
                return (
                  <div key={f.id}>
                    <div
                      style={{ ...S.moveRow, ...(moveModal.program.folderId === f.id ? S.moveRowCurrent : {}) }}
                      onClick={() => !movingId && doMoveProgram(f.id)}
                    >
                      <Folder size={15} style={{ color: '#9aa0a8' }} />
                      <span style={{ flex: 1 }}>{f.name}</span>
                      {moveModal.program.folderId === f.id && <span style={S.moveCurrent}>current</span>}
                      {movingId === f.id && <span style={S.moveCurrent}>moving…</span>}
                    </div>
                    {kids.map(c => (
                      <div
                        key={c.id}
                        style={{ ...S.moveRow, paddingLeft: 34, ...(moveModal.program.folderId === c.id ? S.moveRowCurrent : {}) }}
                        onClick={() => !movingId && doMoveProgram(c.id)}
                      >
                        <Folder size={14} style={{ color: '#9aa0a8' }} />
                        <span style={{ flex: 1 }}>{c.name}</span>
                        {moveModal.program.folderId === c.id && <span style={S.moveCurrent}>current</span>}
                        {movingId === c.id && <span style={S.moveCurrent}>moving…</span>}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            {moveError && <div style={{ color: '#E63946', fontSize: 13, marginTop: 10 }}>{moveError}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button className="btn btn-secondary" onClick={() => setMoveModal(null)} disabled={!!movingId}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Rename folder modal ---- */}
      {renameModal && (
        <div className="modal-overlay" onClick={() => !savingRename && setRenameModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Rename Folder</div>
              <button className="modal-close" onClick={() => !savingRename && setRenameModal(null)}>×</button>
            </div>
            <div className="form-group">
              <label className="form-label">Folder name</label>
              <input className="form-input" autoFocus value={renameName}
                onChange={e => setRenameName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && renameName.trim() && !savingRename) submitRename(); }} />
            </div>
            {renameError && <div style={{ color: '#E63946', fontSize: 13, marginBottom: 12 }}>{renameError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn btn-secondary" onClick={() => setRenameModal(null)} disabled={savingRename}>Cancel</button>
              <button className="btn btn-primary" onClick={submitRename} disabled={savingRename || !renameName.trim()}>
                {savingRename ? 'Saving…' : 'Rename'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Delete folder modal (blocks if non-empty via deleteFolder guard) ---- */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => !deletingFolder && setDeleteModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Delete Folder</div>
              <button className="modal-close" onClick={() => !deletingFolder && setDeleteModal(null)}>×</button>
            </div>
            <div style={{ fontSize: 14, color: '#1a1a2e', marginBottom: 8 }}>
              Delete <strong>“{deleteModal.folder.name}”</strong>?
            </div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
              This only removes the folder. Programs are never deleted — they stay in your library. Any programs or subfolders inside must be moved out first.
            </div>
            {deleteError && <div style={{ color: '#E63946', fontSize: 13, marginBottom: 12 }}>{deleteError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn btn-secondary" onClick={() => setDeleteModal(null)} disabled={deletingFolder}>Cancel</button>
              <button className="btn btn-primary" style={{ background: '#c22f2f', borderColor: '#c22f2f' }} onClick={submitDelete} disabled={deletingFolder}>
                {deletingFolder ? 'Deleting…' : 'Delete Folder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Delete PROGRAM modal ---- */}
      {deleteProgramModal && (
        <div className="modal-overlay" onClick={() => !busyProgram && setDeleteProgramModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Delete Program</div>
              <button className="modal-close" onClick={() => !busyProgram && setDeleteProgramModal(null)}>×</button>
            </div>
            <div style={{ fontSize: 14, color: '#1a1a2e', marginBottom: 8 }}>
              Delete <strong>“{deleteProgramModal.program.name}”</strong>?
            </div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
              This permanently removes the program and cannot be undone. Any screens assigned to it will need a new assignment.
            </div>
            {programActionError && <div style={{ color: '#E63946', fontSize: 13, marginBottom: 12 }}>{programActionError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn btn-secondary" onClick={() => setDeleteProgramModal(null)} disabled={busyProgram}>Cancel</button>
              <button className="btn btn-primary" style={{ background: '#c22f2f', borderColor: '#c22f2f' }} onClick={deleteProgram} disabled={busyProgram}>
                {busyProgram ? 'Deleting…' : 'Delete Program'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Assign to Screen modal (reuses existing contentType/assignedContent) ---- */}
      {assignModal && (
        <div className="modal-overlay" onClick={() => !assigningId && setAssignModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Assign “{assignModal.program.name || 'Program'}” to a screen</div>
              <button className="modal-close" onClick={() => !assigningId && setAssignModal(null)}>×</button>
            </div>
            {screens.length === 0 ? (
              <div style={{ fontSize: 13, color: '#9aa0a8', padding: '16px 4px' }}>No screens found.</div>
            ) : (
              <div style={S.movePicker}>
                {screens.map(sc => {
                  const isThis = sc.contentType === 'program' && sc.assignedContent === assignModal.program.id;
                  return (
                    <div key={sc.id}
                      style={{ ...S.moveRow, ...(isThis ? S.moveRowCurrent : {}) }}
                      onClick={() => !assigningId && !isThis && assignToScreen(sc)}
                    >
                      <MonitorPlay size={15} style={{ color: '#9aa0a8' }} />
                      <span style={{ flex: 1 }}>{sc.screenName || 'Screen'}</span>
                      {assignedOk === sc.id && <span style={{ ...S.moveCurrent, color: '#1a7f37' }}>✓ assigned</span>}
                      {isThis && assignedOk !== sc.id && <span style={S.moveCurrent}>current</span>}
                      {assigningId === sc.id && <span style={S.moveCurrent}>assigning…</span>}
                    </div>
                  );
                })}
              </div>
            )}
            {assignError && <div style={{ color: '#E63946', fontSize: 13, marginTop: 10 }}>{assignError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button className="btn btn-secondary" onClick={() => setAssignModal(null)} disabled={!!assigningId}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Cover image editor modal ---- */}
      {coverModal && (
        <CoverImageEditor
          program={coverModal.program}
          onClose={() => setCoverModal(null)}
          onSave={saveCover}
        />
      )}

      {/* ---- Tags editor modal ---- */}
      {tagsModal && (
        <TagEditor
          program={tagsModal.program}
          allTags={allTags}
          onClose={() => setTagsModal(null)}
          onSave={saveTags}
        />
      )}
    </div>
  );
}

// ---- detail panel for the selected program (right rail) ----
// Shows where the program actually LIVES (folder path from folderId, not its name),
// plus tags/duration/clips/notes and an action stack. Actions are STUBBED (Piece 4).
function ProgramDetail({ program, folderPath, fmtDuration, onEdit, onChangeFolder, onAssign, onDuplicate, onDelete, busy, onChangeCover, onEditTags }) {
  const clipCount = program.composition?.length || 0;
  const tags = Array.isArray(program.tags) ? program.tags : [];
  const accent = program.theme?.accent || '#E63946';
  const stub = (label) => () => alert(`${label} — wiring in Piece 4`);

  return (
    <div style={S.detail}>
      {/* cover: branded placeholder now; real COVER IMAGE upload wired in Piece 5.
          Upload will live BOTH here (quick change) and in the Builder Settings column,
          reusing the equipment-style image upload, writing a coverImageUrl field. */}
      <div
        style={{ ...S.dcover, background: program.coverImageUrl ? '#1a1d21' : `linear-gradient(135deg, #1f2329 0%, ${accent}33 100%)` }}
        onMouseEnter={e => { const o = e.currentTarget.querySelector('[data-cover-overlay]'); if (o) o.style.opacity = '1'; }}
        onMouseLeave={e => { const o = e.currentTarget.querySelector('[data-cover-overlay]'); if (o) o.style.opacity = '0'; }}
      >
        {program.coverImageUrl ? (
          <img src={program.coverImageUrl} alt={program.name}
            style={{ ...S.dcoverImg, objectPosition: `${program.coverPosX ?? 50}% ${program.coverPosY ?? 50}%`, transform: `scale(${program.coverZoom || 1})`, transformOrigin: `${program.coverPosX ?? 50}% ${program.coverPosY ?? 50}%` }} />
        ) : (
          <div style={S.pthumbBadge}><Activity size={24} color="#fff" style={{ opacity: 0.9 }} /></div>
        )}
        {/* hover overlay: change/upload cover (STUB — Piece 5) */}
        <div data-cover-overlay style={S.dcoverOverlay} onClick={onChangeCover}>
          <ImagePlus size={18} />
          <span style={{ fontSize: 12, fontWeight: 600 }}>
            {program.coverImageUrl ? 'Change Cover' : 'Add Cover'}
          </span>
        </div>
      </div>

      <div style={S.dname}>{program.name || 'Untitled Program'}</div>

      {/* FOLDER PATH — where it actually lives */}
      <div style={S.drow}>
        <span style={S.dico}><Folder size={15} /></span>
        <div style={S.dcol}>
          <div style={S.dlab}>Folder</div>
          <div style={S.dval}>{folderPath}</div>
        </div>
        <span style={S.dedit} onClick={onChangeFolder}>Change</span>
      </div>

      {/* TAGS */}
      <div style={S.drow}>
        <span style={S.dico}>#</span>
        <div style={S.dcol}>
          <div style={S.dlab}>Tags</div>
          {tags.length ? (
            <div style={S.dtags}>{tags.map((t, i) => <span key={i} style={{ ...S.tag, background: tagBg(t), color: tagColor(t) }}>{t}</span>)}</div>
          ) : (
            <div style={S.dvalMuted}>No tags</div>
          )}
        </div>
        <span style={S.dedit} onClick={onEditTags}>Edit</span>
      </div>

      {/* DURATION */}
      <div style={S.drow}>
        <span style={S.dico}><Clock size={15} /></span>
        <div style={S.dcol}>
          <div style={S.dlab}>Total Duration</div>
          <div style={S.dval}>{fmtDuration(program.totalDurationMs)}</div>
        </div>
      </div>

      {/* CLIPS */}
      <div style={S.drow}>
        <span style={S.dico}><List size={15} /></span>
        <div style={S.dcol}>
          <div style={S.dlab}>Program Clips</div>
          <div style={S.dval}>{clipCount} clip{clipCount === 1 ? '' : 's'}</div>
        </div>
      </div>

      {/* NOTES (only if present) */}
      {program.notes && (
        <div style={S.drow}>
          <span style={S.dico}>✎</span>
          <div style={S.dcol}>
            <div style={S.dlab}>Notes</div>
            <div style={S.dvalMuted}>{program.notes}</div>
          </div>
        </div>
      )}

      {/* action stack (stubbed — Piece 4) */}
      <div style={S.dactions}>
        <button className="btn btn-primary" style={S.dbtn} onClick={onEdit}>
          <Pencil size={15} /> Edit Program
        </button>
        <button className="btn btn-secondary" style={S.dbtn} onClick={onAssign}>
          <MonitorPlay size={15} /> Assign to Screen
        </button>
        <button className="btn btn-secondary" style={S.dbtn} onClick={onDuplicate} disabled={busy}>
          <Copy size={15} /> {busy ? 'Working…' : 'Duplicate'}
        </button>
        <button className="btn btn-secondary" style={{ ...S.dbtn, color: '#c22f2f', borderColor: '#f0c9c9' }} onClick={onDelete} disabled={busy}>
          <Trash2 size={15} /> Delete
        </button>
      </div>
    </div>
  );
}

// ---- a single program card (lightweight; NOT the heavy .card padding) ----
// Thumbnail is a CLEAN BRANDED PLACEHOLDER (accent-tinted). A program's real cover
// image (uploaded in the Builder — its own later piece) drops into the same slot.
// We deliberately do NOT use clip sprites here: a program is a sequence, not one clip,
// and the cropped clip footage read as an exercise tile, not a program cover.
// Card structure is kept aligned with the approved mockup so the richer footer
// (updated-date + favorite/duplicate/more) and cover image extend it without rework.
function ProgramCard({ program, selected, fmtDuration, onClick }) {
  const clipCount = program.composition?.length || 0;
  const tags = Array.isArray(program.tags) ? program.tags : [];
  const accent = program.theme?.accent || '#E63946';
  const [hover, setHover] = useState(false);

  // Fully declarative — no imperative DOM style mutation (that was re-applying a
  // gray border and stranding it). Rest = NO line (transparent border) + soft shadow;
  // hover AND selected = the faint red line.
  const cardStyle = {
    ...S.pcard,
    ...((selected || hover) ? S.pcardSel : {}),
  };

  return (
    <div
      style={cardStyle}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* thumbnail — the program's COVER IMAGE (with stored framing) if set,
          else a clean branded placeholder. Framing = zoom/pos rendered via CSS. */}
      <div style={{ ...S.pthumb, background: program.coverImageUrl ? '#1a1d21' : `linear-gradient(135deg, #1f2329 0%, ${accent}33 100%)` }}>
        {program.coverImageUrl ? (
          <img src={program.coverImageUrl} alt={program.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover',
              objectPosition: `${program.coverPosX ?? 50}% ${program.coverPosY ?? 50}%`,
              transform: `scale(${program.coverZoom || 1})`, transformOrigin: `${program.coverPosX ?? 50}% ${program.coverPosY ?? 50}%` }} />
        ) : (
          <div style={S.pthumbBadge}>
            <Activity size={22} color="#fff" style={{ opacity: 0.9 }} />
          </div>
        )}
      </div>

      {/* body */}
      <div style={S.pbody}>
        <div style={S.pname}>{program.name || 'Untitled Program'}</div>
        {tags.length > 0 && (
          <div style={S.ptags}>
            {tags.slice(0, 4).map((t, i) => <span key={i} style={{ ...S.tag, background: tagBg(t), color: tagColor(t) }}>{t}</span>)}
            {tags.length > 4 && <span style={S.tagMore}>+{tags.length - 4}</span>}
          </div>
        )}
        <div style={S.pmeta}>
          <span>⏱ {fmtDuration(program.totalDurationMs)}</span>
          <span>▤ {clipCount} clip{clipCount === 1 ? '' : 's'}</span>
        </div>
      </div>
    </div>
  );
}

// ---- a single folder-tree row ----
function FolderRow({ icon, caret, label, count, active, onClick, onCaret, onAddChild, onRename, onDelete, child }) {
  const [hover, setHover] = useState(false);
  const hasActions = onAddChild || onRename || onDelete;
  return (
    <div
      style={{ ...S.foldRow, ...(child ? S.foldRowChild : {}), ...(active ? S.foldRowActive : {}) }}
      onClick={onClick}
      onMouseEnter={e => { setHover(true); if (!active) e.currentTarget.style.background = '#f1f5f9'; }}
      onMouseLeave={e => { setHover(false); if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      {caret !== undefined && caret !== null
        ? <span style={S.caret} onClick={onCaret}>{caret}</span>
        : <span style={S.caretSpacer} />}
      <span style={{ ...S.foldIcon, ...(active ? { color: '#E63946' } : {}) }}>{icon}</span>
      <span style={S.foldLabel}>{label}</span>
      {hasActions && hover ? (
        <span style={S.foldActions}>
          {onAddChild && <span style={S.foldAct} title="Add subfolder" onClick={onAddChild}><Plus size={13} /></span>}
          {onRename && <span style={S.foldAct} title="Rename" onClick={onRename}><Pencil size={12} /></span>}
          {onDelete && <span style={S.foldAct} title="Delete" onClick={onDelete}><Minus size={13} /></span>}
        </span>
      ) : (
        <span style={S.foldCount}>{count}</span>
      )}
    </div>
  );
}

// ---- inline styles: layout/proportions only; visual tokens come from App.css classes ----
const S = {
  headerActions: { display: 'flex', gap: 10, flexShrink: 0 },
  filterBar: { position: 'relative', flex: 1, display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #e6e8ec', borderRadius: 8, padding: '6px 12px', margin: '0 24px' },
  filterIconBtn: { border: 'none', background: 'none', cursor: 'pointer', color: '#9aa0a8', padding: 5, borderRadius: 6, display: 'flex' },
  filterIconActive: { background: '#E63946', color: '#fff' },
  filterInput: { flex: 1, border: 'none', outline: 'none', fontSize: 14, color: '#1a1a2e', background: 'transparent' },
  filterTagArea: { flex: 1, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', minHeight: 26, cursor: 'text' },
  filterClear: { border: 'none', background: 'none', cursor: 'pointer', color: '#9aa0a8', padding: 4, display: 'flex' },
  filterTagDropdown: { position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: '#fff', border: '1px solid #e6e8ec', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 280, overflowY: 'auto', zIndex: 20 },
  tagDropdownDone: { padding: '9px 12px', textAlign: 'center', color: '#E63946', fontWeight: 600, fontSize: 13, cursor: 'pointer', borderTop: '1px solid #f0f1f4' },
  cols: { display: 'grid', gridTemplateColumns: '230px 1fr 300px', gap: 16, alignItems: 'start' },

  leftPanel: { padding: 12 },
  foldHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 6px 10px' },
  foldHeadLabel: { fontSize: 12, fontWeight: 700, color: '#5b6169', textTransform: 'uppercase', letterSpacing: '.4px' },
  iconBtn: { border: 'none', background: 'none', cursor: 'pointer', color: '#E63946', padding: 4, display: 'flex', borderRadius: 4 },
  sep: { height: 1, background: '#eef0f3', margin: '8px 4px' },
  foldLoading: { fontSize: 13, color: '#9aa0a8', padding: '8px 10px' },
  foldEmpty: { fontSize: 12, color: '#9aa0a8', padding: '8px 10px', lineHeight: 1.4 },

  foldRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 8px', borderRadius: 6, cursor: 'pointer', color: '#1a1a2e', fontWeight: 500, userSelect: 'none' },
  foldRowChild: { paddingLeft: 28 },
  foldRowActive: { background: '#fef2f2', color: '#E63946', fontWeight: 600 },
  caret: { width: 14, display: 'flex', color: '#9aa0a8', cursor: 'pointer' },
  caretSpacer: { width: 14, display: 'inline-block' },
  foldIcon: { display: 'flex', color: '#9aa0a8' },
  foldLabel: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 14 },
  addChild: { display: 'flex', color: '#c7ccd1', padding: 2, borderRadius: 4 },
  foldActions: { display: 'flex', alignItems: 'center', gap: 2 },
  foldAct: { display: 'flex', color: '#9aa0a8', padding: 3, borderRadius: 4, cursor: 'pointer' },
  foldCount: { color: '#9aa0a8', fontSize: 12, fontWeight: 500 },

  centerPanel: {},
  centerHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, padding: '0 2px' },
  centerTitle: { fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'baseline', gap: 8 },
  centerCount: { color: '#9aa0a8', fontSize: 13, fontWeight: 500 },
  gridPlaceholder: { minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9aa0a8', fontSize: 14 },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 },
  pcard: { background: '#fff', borderWidth: '1px', borderStyle: 'solid', borderColor: 'transparent', borderRadius: 0, overflow: 'hidden', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.10)', transition: 'box-shadow .12s' },
  pcardSel: { borderColor: '#E63946', boxShadow: '0 2px 6px rgba(0,0,0,0.10)' },
  pthumb: { height: 118, overflow: 'hidden', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  pthumbBadge: { width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  pbody: { padding: '10px 11px 12px' },
  pname: { fontWeight: 700, fontSize: 14, marginBottom: 7, lineHeight: 1.25, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  ptags: { display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 },
  tag: { background: '#eef1f5', color: '#4a5563', borderRadius: 4, padding: '2px 7px', fontSize: 11, fontWeight: 600 },
  tagChipRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6, minHeight: 26 },
  tagChip: { display: 'inline-flex', alignItems: 'center', gap: 5, borderRadius: 5, padding: '3px 8px', fontSize: 12, fontWeight: 600 },
  tagDot: { width: 8, height: 8, borderRadius: '50%', display: 'inline-block' },
  tagX: { cursor: 'pointer', marginLeft: 2, fontWeight: 700, opacity: 0.7 },
  tagSuggest: { border: '1px solid #e6e8ec', borderRadius: 0, marginTop: 8, maxHeight: 200, overflowY: 'auto' },
  tagSuggestRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', cursor: 'pointer', fontSize: 14, borderBottom: '1px solid #f0f1f4' },
  tagMore: { color: '#9aa0a8', fontSize: 11, fontWeight: 600, alignSelf: 'center' },
  pmeta: { display: 'flex', gap: 14, color: '#9aa0a8', fontSize: 12 },
  newCard: { border: '2px dashed #d7dbe0', borderRadius: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 190, cursor: 'pointer', color: '#9aa0a8', gap: 6, textAlign: 'center', padding: 16, transition: 'border-color .12s, color .12s' },
  emptyGrid: { gridColumn: '1 / -1', color: '#9aa0a8', fontSize: 13, padding: '24px 4px' },

  rightPanel: { minHeight: 300, padding: 0, overflow: 'hidden' },
  detail: { display: 'flex', flexDirection: 'column' },
  dcover: { height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', cursor: 'pointer' },
  dcoverImg: { width: '100%', height: '100%', objectFit: 'cover' },
  dcoverOverlay: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#fff', background: 'rgba(0,0,0,0.45)', opacity: 0, transition: 'opacity .15s' },
  dname: { fontSize: 17, fontWeight: 700, color: '#1a1a2e', padding: '14px 16px 6px' },
  drow: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '11px 16px', borderTop: '1px solid #f0f1f4' },
  dico: { width: 16, color: '#9aa0a8', textAlign: 'center', marginTop: 1, fontSize: 14, display: 'flex', justifyContent: 'center' },
  dcol: { flex: 1 },
  dlab: { color: '#9aa0a8', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.3px' },
  dval: { fontSize: 14, fontWeight: 500, marginTop: 3, color: '#1a1a2e' },
  dvalMuted: { fontSize: 13, fontWeight: 400, marginTop: 3, color: '#5b6169', lineHeight: 1.4 },
  dtags: { display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 4 },
  dedit: { color: '#E63946', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  dactions: { display: 'flex', flexDirection: 'column', gap: 8, padding: '16px' },
  dbtn: { justifyContent: 'center', width: '100%' },
  movePicker: { maxHeight: 320, overflowY: 'auto', border: '1px solid #e6e8ec', borderRadius: 0 },
  moveRow: { display: 'flex', alignItems: 'center', gap: 9, padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f1f4', fontSize: 14, color: '#1a1a2e' },
  moveRowCurrent: { background: '#fef2f2', color: '#E63946', fontWeight: 600, cursor: 'default' },
  moveCurrent: { fontSize: 11, fontWeight: 600, color: '#9aa0a8' },

  detailPlaceholder: { height: '100%', minHeight: 268, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
};

export default Programs;